import { moduleNames } from '@va/constants';
import { isContentAnalysisModuleEnabled } from '@va/dashboard/selectors/core';
import { getUserRole } from '@va/standalone/shared/selectors';
import { LoaderBar } from '@va/ui/design-system';
import {
  featureFlagOverrideStorageKeys,
  FlagsmithFeatureFlags,
  isEcomModuleEnabled,
  LocalStorage,
} from '@va/util/helpers';
import { useIsFeatureEnabled } from '@va/util/hooks';
import { ComponentType, lazy, Suspense } from 'react';
import { useSelector } from 'react-redux';
import { Redirect, Route, Switch } from 'react-router-dom';
import WebsiteRoute from './WebsiteRoute';

export type LazyLoadedComponent = { default: ComponentType<any> };

const AccountSettingsTab = lazy(() => import('./AccountSettingsTab'));

const OverviewTab = lazy(() => import('@va/dashboard/tabs/overview'));
const RedirectToOverview = lazy(() => import('@va/dashboard/tabs/overview/redirect'));
const PagesTab = lazy(() => import('@va/dashboard/tabs/pages'));

const CampaignsTab = lazy(() => import('@va/dashboard/tabs/campaigns'));

const GeneralSettingsTab = lazy(() => import('aaas/components/tabs/general-settings/GeneralSettingsTab'));
const ConversionsSettingsTab = lazy(() => import('@va/dashboard/tabs/conversions-settings'));
const PageSettingsTab = lazy(() => import('@va/dashboard/tabs/page-settings'));
const ConsentSettingsTab = lazy(() => import('@va/dashboard/tabs/consent'));
const ExportTab = lazy(async () => (await import('@va/dashboard/tabs/export')) as LazyLoadedComponent);
const UpgradePlanPage = lazy(() => import('aaas/components/pages/UpgradePlan'));
const SubscriptionWebsiteSettingsTab = lazy(
  async () => (await import('@va/aaas/tabs/subscription-website-settings')) as LazyLoadedComponent,
);

const MetaDataAnalyticsTab = lazy(() =>
  import('@va/dashboard/feature/metadata-analytics').then((module): LazyLoadedComponent => ({
    default: module.MetaDataAnalyticsPage,
  })),
);
const TrendIntelTab = lazy(() =>
  import('@va/dashboard/feature/trend-intel').then((module): LazyLoadedComponent => ({
    default: module.TrendIntelPage,
  })),
);

const UnsupportedLocalStorage = lazy(
  async () => (await import('@va/dashboard/tabs/unsupported-local-storage')) as LazyLoadedComponent,
);

/** PRIVACY CENTER */
const PrivacyCenterTab = lazy(() => import('@va/dashboard/tabs/privacy-center'));
/** PRIVACY CENTER */

/** VISITORS */
const Visitors = lazy(() => import('@va/dashboard/tabs/visitors'));
const VisitorsHistoryV2 = lazy(() => import('@va/dashboard/tabs/visitors/history'));
/** VISITORS */

const ModulesSettingsPage = lazy(() => import('@va/dashboard/tabs/module-settings'));
const TrafficDetailsPage = lazy(() => import('@va/dashboard/tabs/traffic'));

const OutgoingTrafficTab = lazy(
  async () => (await import('@va/dashboard/tabs/outgoing-traffic')) as LazyLoadedComponent,
);

const ConversionEventsTab = lazy(async () => (await import('@va/dashboard/tabs/conversions')) as LazyLoadedComponent);

const DevicesTab = lazy(() => import('@va/dashboard/tabs/devices'));
const CompetitionTab = lazy(() => import('@va/dashboard/tabs/competition'));
const WebsiteErrorsTab = lazy(
  async () => (await import('@va/dashboard/tabs/website-errors/main')) as LazyLoadedComponent,
);
const MyCampaignDetails = lazy(() => import('@va/dashboard/tabs/campaigns/details'));

/** EVENT TRACKING */
const EventTrackingModuleTab = lazy(() => import('@va/dashboard/tabs/event-tracking/module'));
const ABEDetailsTab = lazy(() =>
  import('@va/dashboard/modules/event-tracking/alarming-behaviour').then((module) => ({
    default: module.ABEDetailsTab,
  })),
);
/** EVENT TRACKING */

const VisitorRecordingsTabV2 = lazy(() => import('@va/dashboard/tabs/recordings'));
const VisitorRecordingsSettingsOnboarding = lazy(() => import('@va/dashboard/tabs/recordings/onboardings'));

/** HEATMAPS */
const HeatmapDetailView = lazy(() => import('@va/dashboard/tabs/heatmaps/details'));
const HeatmapsTab = lazy(() => import('@va/dashboard/tabs/heatmaps'));
const CreateHeatmapPage = lazy(() => import('@va/dashboard/tabs/heatmaps/create'));
/** HEATMAPS */

/**** FUNNELS ****/
const CreateFunnelPage = lazy(() => import('@va/dashboard/tabs/funnels/create'));
const EditFunnelPage = lazy(() => import('@va/dashboard/tabs/funnels/edit'));
const FunnelsTab = lazy(() => import('@va/dashboard/tabs/funnels/main'));
const FunnelDetailsPage = lazy(() => import('@va/dashboard/tabs/funnels/details'));
/**** FUNNELS ****/

const RecordingsDetailsPageV2 = lazy(() => import('@va/dashboard/tabs/recordings/details'));

/**** POLLS ****/
const PollsTab = lazy(() => import('@va/dashboard/tabs/feedback/polls/main'));
const CreatePollPage = lazy(() => import('@va/dashboard/tabs/feedback/polls/create'));
const EditPollPage = lazy(() => import('@va/dashboard/tabs/feedback/polls/edit'));
const PollDetailsPage = lazy(() => import('@va/dashboard/tabs/feedback/polls/details'));
const PollParticipantsResponsesPage = lazy(() => import('@va/dashboard/tabs/feedback/polls/participants'));
/**** POLLS ****/

/**** SURVEYS ****/
const SurveysTab = lazy(() => import('@va/dashboard/tabs/feedback/surveys/main'));
const CreateSurveyPage = lazy(() => import('@va/dashboard/tabs/feedback/surveys/create'));
const EditSurveyPage = lazy(() => import('@va/dashboard/tabs/feedback/surveys/edit'));
const SurveyDetailsPage = lazy(() => import('@va/dashboard/tabs/feedback/surveys/details'));
/**** SURVEYS ****/

/**** E-COM ****/
const EcomOverview = lazy(() => import('@va/dashboard/tabs/ecom/overview'));
const EcomCustomers = lazy(() => import('@va/dashboard/tabs/ecom/customers'));
const EcomOrders = lazy(() => import('@va/dashboard/tabs/ecom/orders'));
const EcomSalesCharts = lazy(() => import('@va/dashboard/tabs/ecom/sales-charts'));
/**** E-COM ****/

const VisitorSegments = lazy(() => import('@va/dashboard/feature/visitor-segments'));

const GenerateImpersonate = lazy(
  async () => (await import('aaas/components/pages/GenerateImpersonate')) as LazyLoadedComponent,
);

const GeneralErrorPage = lazy(() => import('@va/dashboard/tabs/error'));

export default function AaasRoutes() {
  const userRole = useSelector(getUserRole);

  const isMetaDataAnalyticsEnabled = useSelector(isContentAnalysisModuleEnabled);
  const trendIntelEnabled =
    useIsFeatureEnabled(FlagsmithFeatureFlags.TrendIntel) ||
    LocalStorage.getItem(featureFlagOverrideStorageKeys.trendIntelDebug) === 'true';
  return (
    <Suspense fallback={<LoaderBar />}>
      <Switch>
        {/* Admin routes */}
        <Route path='/generate-impersonate' component={GenerateImpersonate} />
        {/* <Route
          path='/impersonate'
          render={props => (
            <ViewImpersonate impersonateData={props.location.state && props.location.state.impersonateData} />
          )}
        /> */}
        {/* <Route path='/onboarding'>
          <Onboarding />
        </Route> */}

        <Route path='/unsupported-localstorage' component={UnsupportedLocalStorage} />
        {/* TODO Add ignore visits pages */}
        {/* <Route path='/visit-settings' component={IgnoreVisitsPage} /> */}
        {/* <Route path='/ignore-visits/:hash' component={IgnoreVisitsPage} /> */}

        <WebsiteRoute exact path='/' component={RedirectToOverview} />
        <WebsiteRoute path='/dashboard' component={OverviewTab} />

        {isEcomModuleEnabled(userRole) && <WebsiteRoute exact path='/ecommerce/overview' component={EcomOverview} />}
        {isEcomModuleEnabled(userRole) && <WebsiteRoute exact path='/ecommerce/customers' component={EcomCustomers} />}
        {isEcomModuleEnabled(userRole) && <WebsiteRoute exact path='/ecommerce/orders' component={EcomOrders} />}
        {isEcomModuleEnabled(userRole) && <WebsiteRoute path='/ecommerce/sales-charts' component={EcomSalesCharts} />}

        <WebsiteRoute
          exact
          path='/visitors/history/:sessionKey'
          component={VisitorsHistoryV2}
          extraProps={{ isWix: true }}
          moduleName={moduleNames.visitorsHistory}
        />
        <WebsiteRoute path='/visitors' component={Visitors} moduleName={moduleNames.visitors} />
        <WebsiteRoute path='/pages' component={PagesTab} moduleName={moduleNames.pages} />
        <WebsiteRoute path='/traffic' component={TrafficDetailsPage} moduleName={moduleNames.trafficStructure} />

        <WebsiteRoute
          path='/outgoing-traffic'
          component={OutgoingTrafficTab}
          moduleName={moduleNames.outgoingTraffic}
        />

        <WebsiteRoute
          path='/conversion-events'
          component={ConversionEventsTab}
          moduleName={moduleNames.conversionEvents}
        />

        <WebsiteRoute exact path='/devices' component={DevicesTab} moduleName={moduleNames.devices} />
        <WebsiteRoute exact path='/competition' component={CompetitionTab} moduleName={moduleNames.competition} />

        <WebsiteRoute
          exact
          // campaignKey is a MD5 value (32 hexadecimal digits)
          path='/campaigns/:campaignKey([A-Fa-f0-9]{32})'
          component={MyCampaignDetails}
          moduleName={moduleNames.utmMyCampaigns}
        />
        <WebsiteRoute
          exact
          path='/website-errors'
          component={WebsiteErrorsTab}
          moduleName={moduleNames.websiteErrors}
        />
        <WebsiteRoute path='/campaigns' component={CampaignsTab} moduleName={moduleNames.utmCampaigns} />
        <WebsiteRoute
          path='/settings/modules'
          component={ModulesSettingsPage}
          moduleName={moduleNames.moduleSettings}
        />
        {/* TODO Add specific settings page */}
        <WebsiteRoute exact path='/settings' component={GeneralSettingsTab} moduleName={moduleNames.websiteSettings} />
        <WebsiteRoute path='/settings/conversions' component={ConversionsSettingsTab} />
        {isMetaDataAnalyticsEnabled && <WebsiteRoute path='/content-analysis' component={MetaDataAnalyticsTab} />}
        {trendIntelEnabled && (
          <WebsiteRoute path='/growth-engine' component={TrendIntelTab} moduleName={moduleNames.trendIntel} />
        )}
        {/* Keeps old bookmarks and links working after the module was renamed */}
        {trendIntelEnabled && (
          <Route
            path='/trend-intel'
            render={({ location }) => (
              <Redirect to={{ ...location, pathname: location.pathname.replace('/trend-intel', '/growth-engine') }} />
            )}
          />
        )}
        <WebsiteRoute exact path='/settings/pages' component={PageSettingsTab} moduleName={moduleNames.specialPages} />
        <WebsiteRoute
          path='/settings/privacy-center'
          component={PrivacyCenterTab}
          moduleName={moduleNames.privacyCenter}
        />

        <WebsiteRoute path='/settings/visitor-segments' component={VisitorSegments} moduleName={moduleNames.segments} />

        <WebsiteRoute exact path='/settings/consent' component={ConsentSettingsTab} moduleName={moduleNames.consent} />

        <WebsiteRoute
          exact
          path='/behaviour/event-tracking/alarming-behavior-events/:trigger(deadClicks|rageClicks|scrolls|mouseMoves|refreshes|uTurns)'
          component={ABEDetailsTab}
          moduleName={moduleNames.eventTracking}
        />

        <WebsiteRoute
          path='/behaviour/event-tracking'
          component={EventTrackingModuleTab}
          moduleName={moduleNames.eventTracking}
        />

        <WebsiteRoute
          exact
          path='/behaviour/recordings'
          component={VisitorRecordingsTabV2}
          moduleName={moduleNames.sessionRecordings}
        />
        <WebsiteRoute
          exact
          path='/behaviour/recordings-onboarding'
          component={VisitorRecordingsSettingsOnboarding}
          moduleName={moduleNames.sessionRecordings}
        />
        <WebsiteRoute
          exact
          path='/behaviour/funnels/create'
          component={CreateFunnelPage}
          moduleName={moduleNames.funnels}
        />
        <WebsiteRoute
          exact
          path='/behaviour/funnels/edit/:funnelId'
          component={EditFunnelPage}
          moduleName={moduleNames.funnels}
        />
        <WebsiteRoute
          exact
          path='/behaviour/funnels/:funnelId([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})'
          component={FunnelDetailsPage}
          moduleName={moduleNames.funnels}
        />
        <WebsiteRoute path='/behaviour/funnels' component={FunnelsTab} moduleName={moduleNames.funnels} />
        <WebsiteRoute
          // 24-character hexadecimal string value
          path='/behaviour/heatmaps/:heatmapId([A-Fa-f0-9]{24})'
          component={HeatmapDetailView}
          hasBackButton
          moduleName={moduleNames.heatmaps}
        />
        <WebsiteRoute
          path='/behaviour/heatmaps/create'
          component={CreateHeatmapPage}
          moduleName={moduleNames.heatmaps}
        />
        <WebsiteRoute path='/behaviour/heatmaps' component={HeatmapsTab} moduleName={moduleNames.heatmaps} />
        {/* Feedback/Polls */}
        <WebsiteRoute exact path='/feedback/polls' component={PollsTab} moduleName={moduleNames.polls} />
        <WebsiteRoute exact path='/feedback/polls/create' component={CreatePollPage} moduleName={moduleNames.polls} />
        <WebsiteRoute exact path='/feedback/polls/:id/edit' component={EditPollPage} moduleName={moduleNames.polls} />
        <WebsiteRoute
          exact
          path='/feedback/polls/:id/visitors/:visitorKey/responses'
          component={PollParticipantsResponsesPage}
          moduleName={moduleNames.polls}
        />
        <WebsiteRoute path='/feedback/polls/:id' component={PollDetailsPage} moduleName={moduleNames.polls} />
        {/* Feedback/Surveys */}
        <WebsiteRoute exact path='/feedback/surveys' component={SurveysTab} moduleName={moduleNames.surveys} />
        <WebsiteRoute
          exact
          path='/feedback/surveys/create'
          component={CreateSurveyPage}
          moduleName={moduleNames.surveys}
        />
        <WebsiteRoute
          exact
          path='/feedback/surveys/:id/edit'
          component={EditSurveyPage}
          moduleName={moduleNames.surveys}
        />
        <WebsiteRoute path='/feedback/surveys/:id' component={SurveyDetailsPage} moduleName={moduleNames.surveys} />
        <WebsiteRoute path='/export' component={ExportTab} moduleName={moduleNames.export} />
        <WebsiteRoute
          exact
          path='/behaviour/recordings/:sessionId'
          component={RecordingsDetailsPageV2}
          moduleName={moduleNames.sessionRecordings}
        />

        <WebsiteRoute path='/upgrade' component={UpgradePlanPage} moduleName={moduleNames.upgrade} />
        <WebsiteRoute
          exact
          path='/settings/subscription'
          component={SubscriptionWebsiteSettingsTab}
          moduleName={moduleNames.subscription}
        />
        <WebsiteRoute path='/my-account' component={AccountSettingsTab} />

        <Route component={GeneralErrorPage} />
      </Switch>
    </Suspense>
  );
}
